// Linked Accounts panel (mig 0014), shown on the account detail page and the
// edit form. Links are one-directional ("this account has X linked to it")
// and save immediately. Unlinking moves a link to Previously Linked with its
// date range; re-linking creates a new record so history is kept.

import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { accounts, customers, lookups } from '@/lib/api';
import type { AccountLink, AccountLinkCreate } from '@frt/shared-types';

function today(): string {
  // Local calendar date (the user's "today"), not UTC.
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function LinkedAccountsPanel({
  accountId,
  canEdit,
  isAdmin,
}: {
  accountId: number;
  canEdit: boolean;
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['account-links', accountId] as const;
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => accounts.links(accountId),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey });
    void queryClient.invalidateQueries({ queryKey: ['accounts'] });
  };

  const linked = data?.linked ?? [];
  const previous = data?.previously_linked ?? [];

  return (
    <div
      className="space-y-4"
      // Inside the account edit form, Enter in a panel input would submit
      // the whole account form; panel actions are explicit buttons instead.
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault();
      }}
    >
      {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
      {isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          <LinkTable
            title="Linked"
            links={linked}
            empty="No linked accounts."
            renderActions={(l) =>
              canEdit ? (
                <LinkActions
                  accountId={accountId}
                  link={l}
                  isAdmin={isAdmin}
                  onDone={refresh}
                />
              ) : null
            }
          />
          {previous.length > 0 && (
            <LinkTable
              title="Previously Linked"
              links={previous}
              empty=""
              renderActions={(l) =>
                isAdmin ? (
                  <DeleteLinkButton accountId={accountId} link={l} onDone={refresh} />
                ) : null
              }
            />
          )}
        </>
      )}
      {canEdit && <AddLinkForm accountId={accountId} onLinked={refresh} />}
    </div>
  );
}

function LinkTable({
  title,
  links,
  empty,
  renderActions,
}: {
  title: string;
  links: AccountLink[];
  empty: string;
  renderActions: (l: AccountLink) => React.ReactNode;
}) {
  const previous = title !== 'Linked';
  return (
    <div className="space-y-1">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
      {links.length === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">Account</th>
                <th className="py-1 pr-3">Bank</th>
                <th className="py-1 pr-3">Product</th>
                <th className="py-1 pr-3">Last 4</th>
                <th className="py-1 pr-3">{previous ? 'Linked period' : 'Linked on'}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.id} className="border-t border-slate-100 align-top">
                  <td className="py-1 pr-3">
                    {l.kind === 'frt' && l.linked_account_id ? (
                      <Link
                        to="/accounts/$id"
                        params={{ id: String(l.linked_account_id) }}
                        className="font-mono text-brand-700 hover:underline"
                      >
                        {l.linked_account_display_name}
                      </Link>
                    ) : (
                      <span className="text-slate-600">Non-FRT account</span>
                    )}
                  </td>
                  <td className="py-1 pr-3">
                    {l.bank_code} <span className="text-slate-500">({l.bank_name})</span>
                  </td>
                  <td className="py-1 pr-3">{l.product_type_code}</td>
                  <td className="py-1 pr-3 font-mono">{l.last4}</td>
                  <td className="py-1 pr-3 font-mono whitespace-nowrap">
                    {previous ? `${l.linked_on} – ${l.unlinked_on}` : l.linked_on}
                  </td>
                  <td className="py-1 text-right">{renderActions(l)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LinkActions({
  accountId,
  link,
  isAdmin,
  onDone,
}: {
  accountId: number;
  link: AccountLink;
  isAdmin: boolean;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(today());
  const unlink = useMutation({
    mutationFn: () => accounts.unlink(accountId, link.id, { unlinked_on: date }),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  if (!open) {
    return (
      <span className="inline-flex gap-2 whitespace-nowrap">
        <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(true)}>
          Unlink
        </Button>
        {isAdmin && <DeleteLinkButton accountId={accountId} link={link} onDone={onDone} />}
      </span>
    );
  }
  return (
    <div className="inline-flex flex-wrap items-center justify-end gap-2 text-sm">
      <label className="flex items-center gap-1">
        <span className="text-slate-600">Unlinked on</span>
        <Input
          type="date"
          value={date}
          min={link.linked_on}
          max={today()}
          onChange={(e) => setDate(e.target.value)}
          className="w-40"
        />
      </label>
      <Button type="button" size="sm" disabled={!date || unlink.isPending} onClick={() => unlink.mutate()}>
        Confirm unlink
      </Button>
      <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(false)}>
        Cancel
      </Button>
      {unlink.error && (
        <span className="w-full text-right text-xs text-red-600">
          {(unlink.error as Error).message}
        </span>
      )}
    </div>
  );
}

function DeleteLinkButton({
  accountId,
  link,
  onDone,
}: {
  accountId: number;
  link: AccountLink;
  onDone: () => void;
}) {
  const del = useMutation({
    mutationFn: () => accounts.deleteLink(accountId, link.id),
    onSuccess: onDone,
  });
  return (
    <Button
      type="button"
      size="sm"
      variant="destructive"
      title="Remove a record entered by mistake (admin only). Use Unlink to keep history."
      disabled={del.isPending}
      onClick={() => {
        if (window.confirm('Delete this linked-account record permanently? Use Unlink to keep history.')) {
          del.mutate();
        }
      }}
    >
      Delete
    </Button>
  );
}

function AddLinkForm({ accountId, onLinked }: { accountId: number; onLinked: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<'frt' | 'external'>('frt');
  const [linkedOn, setLinkedOn] = useState(today());
  // FRT account picker
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [linkedAccountId, setLinkedAccountId] = useState(0);
  // Non-FRT account
  const [bankId, setBankId] = useState(0);
  const [productTypeId, setProductTypeId] = useState(0);
  const [last4, setLast4] = useState('');
  const [addingBank, setAddingBank] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const accountResults = useQuery({
    queryKey: ['accounts', { q: debounced, per_page: 25 }],
    queryFn: () => accounts.list({ q: debounced || undefined, per_page: 25 }),
    enabled: kind === 'frt',
  });
  const bankList = useQuery({
    queryKey: ['customers', { per_page: 200, status: 'active' }],
    queryFn: () => customers.list({ per_page: 200, status: 'active' }),
    enabled: kind === 'external',
  });
  const productTypes = useQuery({
    queryKey: ['lookups', 'product-types'],
    queryFn: () => lookups.productTypes.list(),
    enabled: kind === 'external',
  });

  const link = useMutation({
    mutationFn: (body: AccountLinkCreate) => accounts.link(accountId, body),
    onSuccess: () => {
      setLinkedAccountId(0);
      setSearch('');
      setBankId(0);
      setProductTypeId(0);
      setLast4('');
      setLinkedOn(today());
      onLinked();
    },
  });

  const candidates = (accountResults.data?.data ?? []).filter((a) => a.id !== accountId);
  const ready =
    Boolean(linkedOn) &&
    (kind === 'frt' ? linkedAccountId > 0 : bankId > 0 && productTypeId > 0 && /^\d{1,4}$/.test(last4));

  const submit = () => {
    if (!ready) return;
    link.mutate(
      kind === 'frt'
        ? { kind, linked_account_id: linkedAccountId, linked_on: linkedOn }
        : { kind, frt_customer_id: bankId, product_type_id: productTypeId, last4, linked_on: linkedOn },
    );
  };

  // A <div>, not a <form>: this panel also renders inside the account edit
  // form, and nested forms would submit the outer one.
  return (
    <div className="rounded border border-slate-200 p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="font-medium text-slate-700">Add linked account:</span>
        <label className="flex items-center gap-1">
          <input type="radio" checked={kind === 'frt'} onChange={() => setKind('frt')} />
          Existing FRT test account
        </label>
        <label className="flex items-center gap-1">
          <input type="radio" checked={kind === 'external'} onChange={() => setKind('external')} />
          Non-FRT account
        </label>
      </div>

      {kind === 'frt' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="block text-slate-600">Search FRT accounts</span>
            <Input
              placeholder="Type part of a display name, e.g. ALLY"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="block text-slate-600">FRT account *</span>
            <select
              value={linkedAccountId}
              onChange={(e) => setLinkedAccountId(Number(e.target.value))}
              className={selectClass}
            >
              <option value={0}>
                {accountResults.isLoading ? 'Loading…' : `— select (${candidates.length} shown) —`}
              </option>
              {candidates.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name}
                  {a.account_status !== 'active' ? ` (${a.account_status})` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="block text-sm">
              <span className="block text-slate-600">Ext FI *</span>
              <div className="flex gap-2">
                <select
                  value={bankId}
                  onChange={(e) => setBankId(Number(e.target.value))}
                  className={selectClass}
                >
                  <option value={0}>— select —</option>
                  {(bankList.data?.data ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.code} ({b.name}){b.is_customer ? '' : ' — non-customer'}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => setAddingBank((v) => !v)}
                  title="Add a financial institution that isn't in the list"
                >
                  + New
                </Button>
              </div>
            </div>
            <label className="block text-sm">
              <span className="block text-slate-600">Product type *</span>
              <select
                value={productTypeId}
                onChange={(e) => setProductTypeId(Number(e.target.value))}
                className={selectClass}
              >
                <option value={0}>— select —</option>
                {(productTypes.data?.data ?? [])
                  .filter((p) => p.active)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} ({p.name})
                    </option>
                  ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">Account last 4 *</span>
              <Input
                inputMode="numeric"
                maxLength={4}
                value={last4}
                onChange={(e) => setLast4(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                className="font-mono"
              />
            </label>
          </div>
          {addingBank && (
            <QuickAddBank
              onCancel={() => setAddingBank(false)}
              onCreated={(id) => {
                void queryClient.invalidateQueries({ queryKey: ['customers'] });
                setBankId(id);
                setAddingBank(false);
              }}
            />
          )}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-sm">
          <span className="block text-slate-600">Linked on *</span>
          <Input
            type="date"
            value={linkedOn}
            max={today()}
            onChange={(e) => setLinkedOn(e.target.value)}
            className="w-44"
          />
        </label>
        <Button type="button" disabled={!ready || link.isPending} onClick={submit}>
          Link account
        </Button>
        {link.error && <span className="text-sm text-red-600">{(link.error as Error).message}</span>}
      </div>
    </div>
  );
}

/** Add a financial institution without leaving the page. It is created in the
 *  non-customer category, so it is only offered for linked accounts. */
function QuickAddBank({
  onCreated,
  onCancel,
}: {
  onCreated: (id: number) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const categories = useQuery({
    queryKey: ['lookups', 'frt-categories'],
    queryFn: () => lookups.frtCategories.list(),
  });
  const nonCustomer = categories.data?.data.find((c) => !c.is_customer && c.active);
  const create = useMutation({
    mutationFn: () =>
      customers.create({
        name: name.trim(),
        code: code.trim(),
        ...(nonCustomer ? { frt_category_id: nonCustomer.id } : {}),
      }),
    onSuccess: (c) => onCreated(c.id),
  });

  return (
    <div className="rounded bg-slate-50 p-3 space-y-2">
      <div className="text-sm font-medium text-slate-700">New financial institution</div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="block text-sm flex-1 min-w-48">
          <span className="block text-slate-600">Name *</span>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!codeTouched) setCode(suggestCode(e.target.value));
            }}
            placeholder="e.g. Chase Bank"
          />
        </label>
        <label className="block text-sm w-36">
          <span className="block text-slate-600">Code *</span>
          <Input
            value={code}
            maxLength={16}
            onChange={(e) => {
              setCodeTouched(true);
              setCode(e.target.value.toUpperCase());
            }}
            className="font-mono"
          />
        </label>
        <Button
          type="button"
          size="sm"
          disabled={!name.trim() || !code.trim() || create.isPending || !nonCustomer}
          onClick={() => create.mutate()}
        >
          Save FI
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        Saved under “{nonCustomer?.name ?? 'Non-Customer'}”. If it later becomes an FRT customer,
        change its category on the FRT Customers page — its linked accounts stay attached.
      </p>
      {create.error && <div className="text-sm text-red-600">{(create.error as Error).message}</div>}
    </div>
  );
}

/** "Chase Bank" → "CHASE"; "Bank of America" → "BOA"-style initials when the
 *  first word is generic. Always editable. */
function suggestCode(name: string): string {
  const words = name.toUpperCase().replace(/[^A-Z0-9 ]/g, ' ').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const generic = new Set(['BANK', 'THE', 'FIRST', 'CREDIT', 'FEDERAL', 'NATIONAL']);
  if (words.length > 1 && generic.has(words[0]!)) {
    return words.map((w) => w[0]).join('').slice(0, 8);
  }
  return words[0]!.slice(0, 10);
}

const selectClass = 'block w-full rounded border border-slate-300 bg-white px-2 py-2 text-sm';
